import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type UnitSystem = 'imperial' | 'metric';
export type ViewMode = 'list' | 'grid' | 'map';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

export interface Modal {
  id: string;
  type: string;
  props?: Record<string, unknown>;
}

export interface UIState {
  unitSystem: UnitSystem;
  sidebarCollapsed: boolean;
  viewMode: ViewMode;
  toasts: Toast[];
  modals: Modal[];
  isLoading: boolean;
  loadingMessage: string | null;
}

const initialState: UIState = {
  unitSystem: 'imperial',
  sidebarCollapsed: false,
  viewMode: 'list',
  toasts: [],
  modals: [],
  isLoading: false,
  loadingMessage: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setUnitSystem(state, action: PayloadAction<UnitSystem>) {
      state.unitSystem = action.payload;
    },
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    setViewMode(state, action: PayloadAction<ViewMode>) {
      state.viewMode = action.payload;
    },
    addToast(state, action: PayloadAction<Omit<Toast, 'id'>>) {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      state.toasts.push({ ...action.payload, id });
    },
    removeToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
    clearToasts(state) {
      state.toasts = [];
    },
    openModal(state, action: PayloadAction<Omit<Modal, 'id'>>) {
      const id = `modal-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      state.modals.push({ ...action.payload, id });
    },
    closeModal(state, action: PayloadAction<string>) {
      state.modals = state.modals.filter((m) => m.id !== action.payload);
    },
    closeAllModals(state) {
      state.modals = [];
    },
    setLoading(state, action: PayloadAction<{ isLoading: boolean; message?: string }>) {
      state.isLoading = action.payload.isLoading;
      state.loadingMessage = action.payload.message ?? null;
    },
  },
});

export const {
  setUnitSystem,
  toggleSidebar,
  setSidebarCollapsed,
  setViewMode,
  addToast,
  removeToast,
  clearToasts,
  openModal,
  closeModal,
  closeAllModals,
  setLoading,
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;

// Selectors
export const selectUnitSystem = (state: { ui: UIState }) => state.ui.unitSystem;
export const selectSidebarCollapsed = (state: { ui: UIState }) => state.ui.sidebarCollapsed;
export const selectViewMode = (state: { ui: UIState }) => state.ui.viewMode;
export const selectToasts = (state: { ui: UIState }) => state.ui.toasts;
export const selectModals = (state: { ui: UIState }) => state.ui.modals;
export const selectIsLoading = (state: { ui: UIState }) => state.ui.isLoading;
export const selectLoadingMessage = (state: { ui: UIState }) => state.ui.loadingMessage;
